import React from 'react'
import { RadioGroup as HeadlessRadioGroup, Label } from '@headlessui/react'
import { cn } from '../../utils'
import RadioChip from './RadioChip'

export interface RadioChipOption {
  value: string
  label: React.ReactNode
  disabled?: boolean
  className?: string
  testId?: string
}

export interface RadioChipGroupProps {
  name?: string
  value: string | null
  onChange?: (value: string) => void
  options: RadioChipOption[]
  disabled?: boolean
  className?: string
  chipContainerClassName?: string
  testId?: string
  hasError?: boolean
  readOnly?: boolean
  showOnlySelected?: boolean
  activateEmptyWarning?: boolean
  variant?: 'chip' | 'plain'
}

export default function RadioChipGroup({
  name,
  value,
  onChange,
  options,
  className,
  chipContainerClassName,
  testId,
  disabled = false,
  readOnly = false,
  hasError = false,
  showOnlySelected = false,
  variant = 'chip'
}: Readonly<RadioChipGroupProps>) {
  const controlledValue = value ?? ''

  const visibleOptions =
    showOnlySelected && controlledValue
      ? options.filter((option) => option.value === controlledValue)
      : options

  const chipContainer = (
    <div
      className={cn(
        'flex items-baseline flex-wrap gap-y-0.5',
        chipContainerClassName
      )}
    >
      {visibleOptions.map((option, index) => {
        const isSelected = option.value === controlledValue
        const isChipDisabled = disabled || option.disabled
        const chipShouldBeStruckThrough = controlledValue !== '' && !isSelected

        return (
          <React.Fragment key={option.value}>
            <RadioChip
              value={option.value}
              label={option.label}
              disabled={isChipDisabled}
              hasError={hasError}
              variant={variant}
              readOnly={readOnly}
              isStatic={readOnly}
              isChecked={isSelected}
              className={cn(
                {
                  'line-through !text-cool-gray-700 opacity-70 dark:bg-cool-gray-600 dark:!text-cool-gray-200':
                    chipShouldBeStruckThrough
                },
                option.className
              )}
              testId={
                option.testId || (testId && `${testId}-option-${option.value}`)
              }
            />
            {index < visibleOptions.length - 1 && (
              <span
                aria-hidden="true"
                className="mx-0.25 text-cool-gray-600 dark:text-cool-gray-200"
              >
                /
              </span>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )

  if (readOnly) {
    return (
      <div className={cn('flex flex-col', className)} data-testid={testId}>
        {chipContainer}
      </div>
    )
  }

  return (
    <HeadlessRadioGroup
      value={controlledValue}
      onChange={onChange}
      name={name}
      disabled={disabled}
      className={cn('flex flex-col', className)}
      data-testid={testId}
      aria-invalid={hasError || undefined}
    >
      {name && <Label className="sr-only">{name}</Label>}
      {chipContainer}
    </HeadlessRadioGroup>
  )
}
